/**
 * Cognito authentication wrapper (email/password) using
 * amazon-cognito-identity-js. Handles signup, email verification, login,
 * password reset, and JWT retrieval for API calls.
 */

import {
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  AuthenticationDetails,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { getConfig } from './config.js';

let pool: CognitoUserPool | undefined;
function userPool(): CognitoUserPool {
  if (!pool) {
    const cfg = getConfig();
    pool = new CognitoUserPool({ UserPoolId: cfg.userPoolId, ClientId: cfg.userPoolClientId });
  }
  return pool;
}

function userFor(email: string): CognitoUser {
  return new CognitoUser({ Username: email, Pool: userPool() });
}

/* ---------- dev-only local session ----------------------------------------
 * Active only under `vite dev` AND when the served /config.json opts in — two
 * independent gates.
 *
 * Note the shape of the guards below: `import.meta.env.DEV && devAuth()`, with
 * the literal at every call site rather than hidden inside `devAuth()`. Vite
 * replaces `import.meta.env.DEV` with `false` at build time, so each branch
 * folds to `if (false)` and the helpers here are then tree-shaken out entirely.
 * Putting the check only inside `devAuth()` does NOT work: esbuild will not
 * propagate the constant across a function boundary, so the calls (and these
 * string keys) survive into the bundle. That was measured, not assumed —
 * `packages/web/dev/assert-no-dev-code.mjs` runs on every build and caught
 * exactly that regression.
 *
 * The config gate is the second, independent line of defence: CDK's config.json
 * has no `devAuth` key, so the bypass stays off even if stripping ever failed.
 * Every guard sits BEFORE `userPool()`, which would otherwise throw on a fake
 * pool id.
 * ------------------------------------------------------------------------- */

const DEV_SESSION_KEY = 'irrigation.dev.session';
const DEV_SCENARIO_KEY = 'irrigation.dev.scenario';

function devAuth(): boolean {
  try {
    return getConfig().devAuth === true;
  } catch {
    return false; // config not loaded (unit tests) — never bypass
  }
}

function devRead(): { email: string; token: string } | undefined {
  const raw = localStorage.getItem(DEV_SESSION_KEY);
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as { email: string; token: string };
  } catch {
    return undefined;
  }
}

/**
 * Mint a local session. The scenario rides inside the fake bearer token
 * (`dev.<scenario>.<email>`), which is how the dev API server picks which world
 * to serve — so `api.ts` needs no dev-only code of its own.
 */
function devWrite(email: string): void {
  const scenario = localStorage.getItem(DEV_SCENARIO_KEY) ?? 'healthy';
  console.info('[dev-auth] local session — no Cognito', { email, scenario });
  localStorage.setItem(DEV_SESSION_KEY, JSON.stringify({ email, token: `dev.${scenario}.${email}` }));
}

/**
 * True when Cognito rejected a sign-in because the account exists but its email
 * was never verified — the "signed up, closed the tab, came back" case. Cognito
 * reports this as `UserNotConfirmedException`, on `code` in this SDK and on
 * `name` in others, so both are checked.
 */
export function isUnconfirmedUser(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const { code, name } = err as { code?: unknown; name?: unknown };
  return code === 'UserNotConfirmedException' || name === 'UserNotConfirmedException';
}

export function signUp(email: string, password: string): Promise<void> {
  if (import.meta.env.DEV && devAuth()) return Promise.resolve();
  return new Promise((resolve, reject) => {
    userPool().signUp(
      email,
      password,
      [new CognitoUserAttribute({ Name: 'email', Value: email })],
      [],
      (err) => (err ? reject(err) : resolve()),
    );
  });
}

export function confirmSignUp(email: string, code: string): Promise<void> {
  if (import.meta.env.DEV && devAuth()) return Promise.resolve();
  return new Promise((resolve, reject) => {
    userFor(email).confirmRegistration(code, true, (err) => (err ? reject(err) : resolve()));
  });
}

export function resendCode(email: string): Promise<void> {
  if (import.meta.env.DEV && devAuth()) return Promise.resolve();
  return new Promise((resolve, reject) => {
    userFor(email).resendConfirmationCode((err) => (err ? reject(err) : resolve()));
  });
}

export function signIn(email: string, password: string): Promise<CognitoUserSession> {
  if (import.meta.env.DEV && devAuth()) {
    devWrite(email);
    // App.tsx only checks that this resolves; the session object is unused.
    return Promise.resolve({} as CognitoUserSession);
  }
  return new Promise((resolve, reject) => {
    userFor(email).authenticateUser(
      new AuthenticationDetails({ Username: email, Password: password }),
      {
        onSuccess: (session) => resolve(session),
        onFailure: (err) => reject(err),
      },
    );
  });
}

export function forgotPassword(email: string): Promise<void> {
  if (import.meta.env.DEV && devAuth()) return Promise.resolve();
  return new Promise((resolve, reject) => {
    userFor(email).forgotPassword({ onSuccess: () => resolve(), onFailure: (err) => reject(err) });
  });
}

export function confirmPassword(email: string, code: string, newPassword: string): Promise<void> {
  if (import.meta.env.DEV && devAuth()) return Promise.resolve();
  return new Promise((resolve, reject) => {
    userFor(email).confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function signOut(): void {
  if (import.meta.env.DEV && devAuth()) return void localStorage.removeItem(DEV_SESSION_KEY);
  userPool().getCurrentUser()?.signOut();
}

/**
 * The signed-in user's email address.
 *
 * Deliberately NOT `getUsername()`: the pool is created with
 * `signInAliases: { email: true }`, so Cognito assigns each account a generated
 * UUID as its username and treats the email as an alias. `getUsername()` hands
 * back that UUID, which is what used to show up in the header. The address only
 * exists in the `email` claim of the ID token, hence the async session read.
 */
export function currentEmail(): Promise<string | undefined> {
  if (import.meta.env.DEV && devAuth()) return Promise.resolve(devRead()?.email);
  return currentSession().then((session) => {
    const { email } = session?.getIdToken().payload ?? {};
    return typeof email === 'string' ? email : undefined;
  });
}

/** Resolve a valid ID token for the current user, refreshing if needed. */
export function getIdToken(): Promise<string | undefined> {
  if (import.meta.env.DEV && devAuth()) return Promise.resolve(devRead()?.token);
  return currentSession().then((session) => session?.getIdToken().getJwtToken());
}

/** The current valid session, or undefined when signed out or expired. */
function currentSession(): Promise<CognitoUserSession | undefined> {
  return new Promise((resolve) => {
    const user = userPool().getCurrentUser();
    if (!user) return resolve(undefined);
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve(undefined);
      resolve(session);
    });
  });
}
